import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";

import AttachMoneyOutlinedIcon from "@material-ui/icons/AttachMoneyOutlined";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import StoreIcon from "@material-ui/icons/Store";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import StoreMallDirectoryIcon from "@material-ui/icons/StoreMallDirectory";
import LocalTaxiIcon from "@material-ui/icons/LocalTaxi";
import MotorcycleIcon from "@material-ui/icons/Motorcycle";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import TimelineIcon from "@material-ui/icons/Timeline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import MetricCard from "designV2/components/MetricCard";
import FilterDropdown from "designV2/components/FilterDropdown";
import Panel from "designV2/components/Panel";
import TrendChart from "designV2/components/TrendChart";
import { MetricsSkeleton } from "designV2/components/Skeleton";
import "./superDashboard.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * One aggregate query, mirroring the shop admin dashboard's
 * getShopDashboard — a dedicated backend resolver rather than
 * combining a dozen scattered admin queries in the browser. It sums
 * platform commission directly on Order/TripOrder documents, so the
 * numbers here are real revenue with period-over-period deltas and a
 * daily trend series, not client-side arithmetic on a handful of counts.
 * ------------------------------------------------------------------ */

const GET_SUPER_DASHBOARD = gql`
  query getSuperDashboard($range: SuperDashboardRange!) {
    getSuperDashboard(range: $range) {
      range
      from
      to
      revenue {
        value
        previous
        changePercent
      }
      orders {
        value
        previous
        changePercent
      }
      trips {
        value
        previous
        changePercent
      }
      series {
        date
        revenue
        orders
      }
      restaurant {
        revenue
        count
      }
      grocery {
        revenue
        count
      }
      delivery {
        revenue
        count
      }
      ride {
        revenue
        count
      }
      activeDrivers
      activeUsers
      activeShops
      pendingShopVerifications
      pendingDriverVerifications
      failedPayments
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants + helpers
 * ------------------------------------------------------------------ */

const RANGE_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "LAST_7_DAYS", label: "Last 7 days" },
  { value: "LAST_30_DAYS", label: "Last 30 days" },
  { value: "LAST_90_DAYS", label: "Last 90 days" },
];
const RANGE_LABELS = RANGE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});
const RANGE_COMPARISON = {
  TODAY: "vs yesterday",
  LAST_7_DAYS: "vs previous 7 days",
  LAST_30_DAYS: "vs previous 30 days",
  LAST_90_DAYS: "vs previous 90 days",
};

const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatMoney = (value) => `$${numberFormatter.format(value || 0)}`;
const formatMoneyCompact = (value) => {
  const amount = value || 0;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${Math.round(amount)}`;
};
const formatCount = (value) => new Intl.NumberFormat().format(value || 0);

// A change against a zero baseline is undefined rather than infinite, so the
// backend sends null and no delta is drawn.
const toDelta = (metric, comparison) => {
  if (!metric || metric.changePercent === null || metric.changePercent === undefined) {
    return undefined;
  }
  const rounded = Math.round(metric.changePercent * 10) / 10;
  if (rounded === 0) return { direction: "flat", label: `No change ${comparison}` };
  return {
    direction: rounded > 0 ? "up" : "down",
    label: `${Math.abs(rounded)}% ${comparison}`,
  };
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const DashBoard = () => {
  const [range, setRange] = useState("LAST_30_DAYS");

  const { data, loading, error } = useQuery(GET_SUPER_DASHBOARD, {
    variables: { range },
    fetchPolicy: "network-only",
  });

  // network-only clears `data` whenever the range changes, which would drop
  // the whole page back to a skeleton on every switch. Holding the last
  // successful payload lets the numbers stay put until the new ones land.
  const lastDashboard = useRef(null);
  if (data && data.getSuperDashboard) {
    lastDashboard.current = data.getSuperDashboard;
  }

  if (error) {
    return <ErrorPage />;
  }

  const dashboard = (data && data.getSuperDashboard) || lastDashboard.current;
  const comparison = RANGE_COMPARISON[range];

  const header = (
    <PageHeader
      title="Dashboard"
      description="How the platform is doing across shops, drivers and riders."
      actions={
        <FilterDropdown
          label={RANGE_LABELS[range]}
          options={RANGE_OPTIONS}
          value={range}
          active={false}
          align="right"
          onChange={(e) => setRange(e.target.value)}
        />
      }
    />
  );

  // Covers the first load only — the alert bar, metrics and chart keep
  // their last good numbers while a range change or refetch is in
  // flight, rather than flashing back to a skeleton (and a misleading
  // "0" for a moment).
  if (loading && !dashboard) {
    return (
      <div className="page superDashboardPage">
        <main className="main">
          {header}
          <MetricsSkeleton count={6} />
        </main>
      </div>
    );
  }

  const hasAlerts =
    dashboard.pendingShopVerifications > 0 ||
    dashboard.pendingDriverVerifications > 0 ||
    dashboard.failedPayments > 0;

  const chartPoints = dashboard.series.map((point) => ({
    date: point.date,
    value: point.revenue,
    meta: point.orders,
  }));

  return (
    <div className="page superDashboardPage">
      <main className="main">
        {header}

        {hasAlerts && (
          <div className="superDashboardAlerts">
            {dashboard.pendingShopVerifications > 0 && (
              <Link className="superDashboardAlert warning" to="/superAdmin/shopVerifications">
                <ReportProblemOutlinedIcon />
                <span className="superDashboardAlertText">
                  <strong>{dashboard.pendingShopVerifications}</strong>{" "}
                  {dashboard.pendingShopVerifications === 1 ? "shop" : "shops"} waiting on verification
                </span>
                <span className="superDashboardAlertAction">Review →</span>
              </Link>
            )}
            {dashboard.pendingDriverVerifications > 0 && (
              <Link className="superDashboardAlert warning" to="/superAdmin/verifications">
                <ReportProblemOutlinedIcon />
                <span className="superDashboardAlertText">
                  <strong>{dashboard.pendingDriverVerifications}</strong>{" "}
                  {dashboard.pendingDriverVerifications === 1 ? "driver" : "drivers"} waiting on verification
                </span>
                <span className="superDashboardAlertAction">Review →</span>
              </Link>
            )}
            {dashboard.failedPayments > 0 && (
              <Link className="superDashboardAlert attention" to="/superAdmin/failedPayments">
                <ErrorOutlineIcon />
                <span className="superDashboardAlertText">
                  <strong>{dashboard.failedPayments}</strong>{" "}
                  {dashboard.failedPayments === 1 ? "payment has" : "payments have"} failed
                </span>
                <span className="superDashboardAlertAction">Open →</span>
              </Link>
            )}
          </div>
        )}

        {/* PLATFORM OVERVIEW */}
        <section className="stats">
          <MetricCard
            icon={<AttachMoneyOutlinedIcon />}
            title="Platform revenue"
            value={formatMoney(dashboard.revenue.value)}
            tone="success"
            delta={toDelta(dashboard.revenue, comparison)}
          />
          <MetricCard
            icon={<ShoppingCartIcon />}
            title="Orders"
            value={formatCount(dashboard.orders.value)}
            delta={toDelta(dashboard.orders, comparison)}
          />
          <MetricCard
            icon={<LocalTaxiIcon />}
            title="Trips"
            value={formatCount(dashboard.trips.value)}
            delta={toDelta(dashboard.trips, comparison)}
          />
          <MetricCard icon={<StoreIcon />} title="Active shops" value={formatCount(dashboard.activeShops)} tone="info" />
          <MetricCard icon={<DirectionsCarIcon />} title="Active drivers" value={formatCount(dashboard.activeDrivers)} />
          <MetricCard icon={<PeopleOutlineIcon />} title="Active customers" value={formatCount(dashboard.activeUsers)} />
        </section>

        {/* CHART + LINE OF BUSINESS */}
        <div className="superDashboardGrid">
          <Panel
            title="Revenue over time"
            subtitle={`Daily platform commission across the ${RANGE_LABELS[range].toLowerCase()}`}
          >
            <TrendChart
              data={chartPoints}
              formatValue={(value, isAxis) => (isAxis ? formatMoneyCompact(value) : formatMoney(value))}
              formatDate={(date) => moment(date).format("dddd, MMM D")}
              formatAxisDate={(date) => moment(date).format("MMM D")}
              metaLabel="orders & trips"
              emptyMessage="No activity recorded in this period"
            />
          </Panel>

          <Panel title="Revenue by line of business" subtitle={RANGE_LABELS[range]}>
            <div className="superDashboardRevenueGrid">
              <div className="superDashboardRevenueTile">
                <span className="superDashboardRevenueIcon restaurant">
                  <RestaurantIcon fontSize="small" />
                </span>
                <span className="superDashboardRevenueLabel">Restaurant</span>
                <span className="superDashboardRevenueValue">{formatMoney(dashboard.restaurant.revenue)}</span>
                <span className="superDashboardRevenueMeta">{formatCount(dashboard.restaurant.count)} orders</span>
              </div>
              <div className="superDashboardRevenueTile">
                <span className="superDashboardRevenueIcon grocery">
                  <StoreMallDirectoryIcon fontSize="small" />
                </span>
                <span className="superDashboardRevenueLabel">Purchase</span>
                <span className="superDashboardRevenueValue">{formatMoney(dashboard.grocery.revenue)}</span>
                <span className="superDashboardRevenueMeta">{formatCount(dashboard.grocery.count)} orders</span>
              </div>
              <div className="superDashboardRevenueTile">
                <span className="superDashboardRevenueIcon delivery">
                  <MotorcycleIcon fontSize="small" />
                </span>
                <span className="superDashboardRevenueLabel">Delivery</span>
                <span className="superDashboardRevenueValue">{formatMoney(dashboard.delivery.revenue)}</span>
                <span className="superDashboardRevenueMeta">{formatCount(dashboard.delivery.count)} trips</span>
              </div>
              <div className="superDashboardRevenueTile">
                <span className="superDashboardRevenueIcon ride">
                  <LocalTaxiIcon fontSize="small" />
                </span>
                <span className="superDashboardRevenueLabel">Ride</span>
                <span className="superDashboardRevenueValue">{formatMoney(dashboard.ride.revenue)}</span>
                <span className="superDashboardRevenueMeta">{formatCount(dashboard.ride.count)} trips</span>
              </div>
            </div>
          </Panel>
        </div>

        {/* QUICK LINKS */}
        <Panel title="Jump to">
          <div className="superDashboardQuickLinks">
            <Link className="superDashboardQuickLink" to="/superAdmin/drivers">
              <DirectionsCarIcon fontSize="small" />
              Drivers
            </Link>
            <Link className="superDashboardQuickLink" to="/superAdmin/customers">
              <PeopleOutlineIcon fontSize="small" />
              Customers
            </Link>
            <Link className="superDashboardQuickLink" to="/superAdmin/shopsAndRestaurants">
              <StoreIcon fontSize="small" />
              Shops
            </Link>
            <Link className="superDashboardQuickLink" to="/superAdmin/trips">
              <TimelineIcon fontSize="small" />
              Trips
            </Link>
            <Link className="superDashboardQuickLink" to="/superAdmin/finance">
              <AttachMoneyOutlinedIcon fontSize="small" />
              Finance
            </Link>
            <Link className="superDashboardQuickLink" to="/superAdmin/tickets">
              <ChatBubbleOutlineIcon fontSize="small" />
              Tickets
            </Link>
          </div>
        </Panel>
      </main>
    </div>
  );
};

export default DashBoard;
